import {
  CheckOutlined,
  LoadingOutlined,
  MinusOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js/pure";
import { Spin } from "antd";
import { useCallback, useEffect, useMemo, useState } from "react";
import type React from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router";
import styled from "styled-components";

import {
  AI_QUOTA_PAYWALL_CONTACT_SALES_URL,
  AI_QUOTA_PAYWALL_CREDITS_PER_SEAT,
  BillingPlan,
  AccessMode,
  DeploymentTypeEnum,
} from "@superblocksteam/shared";

import logo from "assets/images/logo.svg";
import { MANAGE_BILLING } from "constants/rbac";
import { useDefaultPricing } from "core/ai/useDefaultPricing";
import envs, { SUPERBLOCKS_UI_DEPLOYMENT_TYPE } from "env";
import { useFeatureFlag } from "hooks/ui";
import { useAuthorizationCheck } from "hooks/ui/rbac/useAuthorizationCheck";
import { LOGOUT_URL } from "legacy/constants/routes";
import { getAccessMode } from "legacy/selectors/usersSelectors";
import { Flag } from "store/slices/featureFlags";
import { selectOnlyOrganization } from "store/slices/organizations/selectors";
import { callServer, HttpMethod } from "store/utils/client";

loadStripe.setLoadParameters({ advancedFraudSignals: false });

const stripePromise = loadStripe(envs.get("SUPERBLOCKS_UI_STRIPE_KEY"));

type BillingInterval = "annual" | "monthly";

const FEATURES = {
  "AI & Credits": [
    "80 AI Credits per AI Builder / month",
    "Clark AI agent for app building",
    "Clark User Memory",
    "AI Credit Packs add-on available",
  ],
  Platform: [
    "Staging and production environments",
    "50+ integrations",
    "1 deployed app per team",
    "Scale team up to 15 Builders",
  ],
};

// --- Styled Components ---

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  display: flex;
  flex-direction: column;
  background-color: #fbfaf9;
  z-index: 100;
  overflow-y: auto;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 32px;
  border-bottom: 1px solid #e5e7eb;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const LogoWrapper = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: linear-gradient(135deg, #575757 0%, #000000 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

const LogoImg = styled.img`
  width: 26px;
  height: auto;
`;

const HeaderTitle = styled.span`
  font-family: "Inter", sans-serif;
  font-weight: 600;
  font-size: 16px;
  color: #131416;
`;

const TalkToSalesButton = styled.button`
  padding: 8px 16px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background: white;
  font-family: "Inter", sans-serif;
  font-weight: 500;
  font-size: 14px;
  color: #374151;
  cursor: pointer;
  &:hover {
    background: #f9fafb;
  }
`;

const Content = styled.div`
  display: flex;
  flex: 1;
  width: 100%;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const LeftPanel = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  padding: 40px 48px;

  @media (max-width: 768px) {
    padding: 32px 24px;
  }
`;

const LeftPanelInner = styled.div`
  width: 100%;
  max-width: 560px;
`;

const RightPanel = styled.div`
  width: 380px;
  flex-shrink: 0;
  padding: 40px 32px;
  background: #f4f3f1;
  border-left: 1px solid #e5e7eb;

  @media (max-width: 768px) {
    width: 100%;
    border-left: none;
    border-top: 1px solid #e5e7eb;
    padding: 32px 24px;
  }
`;

const Title = styled.h1`
  font-family: "Inter", sans-serif;
  font-weight: 600;
  font-size: 24px;
  line-height: 32px;
  color: #111827;
  margin: 0 0 4px;
`;

const TitleSubheader = styled.p`
  font-family: "Inter", sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #6b7280;
  margin: 0 0 4px;
`;

const SkipLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  font-family: "Inter", sans-serif;
  font-weight: 400;
  font-size: 14px;
  color: #6b7280;
  cursor: pointer;
  text-decoration: underline;
  margin-bottom: 24px;
  display: block;
  &:hover {
    color: #374151;
  }
`;

const SectionLabel = styled.div`
  font-family: "Inter", sans-serif;
  font-weight: 400;
  font-size: 14px;
  color: #6b7280;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SaveBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 10px;
  border-radius: 100px;
  background: rgba(0, 153, 255, 0.08);
  color: #0099ff;
  font-size: 12px;
  font-weight: 500;
`;

const IntervalToggle = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 12px;
  margin-bottom: 32px;
`;

const IntervalOption = styled.button<{ $active: boolean }>`
  flex: 1;
  padding: 16px;
  border: 2px solid ${(p) => (p.$active ? "#0099FF" : "#e5e7eb")};
  border-radius: 12px;
  background: white;
  cursor: pointer;
  text-align: left;
  position: relative;

  &:hover {
    border-color: ${(p) => (p.$active ? "#0099FF" : "#d1d5db")};
  }
`;

const IntervalLabel = styled.div`
  font-family: "Inter", sans-serif;
  font-weight: 500;
  font-size: 14px;
  color: #374151;
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const IntervalPrice = styled.div`
  font-family: "Inter", sans-serif;
  font-weight: 600;
  font-size: 20px;
  color: #0099ff;
`;

const IntervalUnit = styled.span`
  font-weight: 400;
  font-size: 14px;
  color: #6b7280;
`;

const IntervalSub = styled.div`
  font-family: "Inter", sans-serif;
  font-weight: 400;
  font-size: 13px;
  color: #9ca3af;
  margin-top: 2px;
`;

const RadioDot = styled.div<{ $active: boolean }>`
  position: absolute;
  top: 16px;
  right: 16px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 2px solid ${(p) => (p.$active ? "#0099FF" : "#d1d5db")};
  display: flex;
  align-items: center;
  justify-content: center;

  &::after {
    content: "";
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${(p) => (p.$active ? "#0099FF" : "transparent")};
  }
`;

const SeatSelector = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
`;

const StepperButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid #d1d5db;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #374151;
  &:hover:not(:disabled) {
    background: #f9fafb;
    border-color: #9ca3af;
  }
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const SeatCount = styled.span`
  font-family: "Inter", sans-serif;
  font-weight: 600;
  font-size: 18px;
  color: #111827;
  min-width: 32px;
  text-align: center;
`;

const IncludesNote = styled.div`
  font-family: "Inter", sans-serif;
  font-weight: 400;
  font-size: 13px;
  color: #6b7280;
  margin-top: -16px;
  margin-bottom: 24px;
`;

const BreakdownRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
  font-family: "Inter", sans-serif;
  font-size: 13px;
  color: #6b7280;
`;

const PaymentSection = styled.div`
  margin-bottom: 24px;
`;

const PaymentTitle = styled.h2`
  font-family: "Inter", sans-serif;
  font-weight: 600;
  font-size: 16px;
  color: #111827;
  margin: 0 0 4px;
`;

const PaymentSubtitle = styled.p`
  font-family: "Inter", sans-serif;
  font-weight: 400;
  font-size: 13px;
  color: #9ca3af;
  margin: 0 0 16px;
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 14px 24px;
  background-color: #131416;
  color: white;
  border: none;
  border-radius: 10px;
  font-family: "Inter", sans-serif;
  font-weight: 600;
  font-size: 16px;
  cursor: pointer;
  margin-top: 8px;
  position: relative;
  box-shadow:
    0px 0px 0px 1px black,
    0px 1px 2px 0px rgba(27, 30, 34, 0.08),
    0px 3px 8px 1px rgba(14, 20, 27, 0.16);

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: inherit;
    box-shadow: inset 0px 1px 0px 0px rgba(255, 255, 255, 0.3);
    pointer-events: none;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Disclaimer = styled.p`
  margin: 12px 0 0;
  font-family: "Inter", sans-serif;
  font-size: 12px;
  line-height: 18px;
  color: #6b7280;
  text-align: center;
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 16px 0;
  font-family: "Inter", sans-serif;
  font-size: 12px;
  color: #9ca3af;
`;

const FooterLink = styled.a`
  color: #6b7280;
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;

const SummaryLabel = styled.div`
  font-family: "Inter", sans-serif;
  font-weight: 600;
  font-size: 11px;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: #6b7280;
  margin-bottom: 16px;
`;

const PlanName = styled.div`
  font-family: "Inter", sans-serif;
  font-weight: 700;
  font-size: 20px;
  color: #111827;
  margin-bottom: 4px;
`;

const PlanSubline = styled.div`
  font-family: "Inter", sans-serif;
  font-weight: 400;
  font-size: 14px;
  color: #6b7280;
  margin-bottom: 24px;
`;

const FeatureCategory = styled.div`
  font-family: "Inter", sans-serif;
  font-weight: 600;
  font-size: 13px;
  color: #374151;
  margin-bottom: 8px;
  margin-top: 20px;
  &:first-of-type {
    margin-top: 0;
  }
`;

const FeatureItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-family: "Inter", sans-serif;
  font-weight: 400;
  font-size: 13px;
  color: #374151;
  margin-bottom: 6px;
  line-height: 20px;
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid #e5e7eb;
  margin: 24px 0 16px;
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`;

const SummaryKey = styled.span`
  font-family: "Inter", sans-serif;
  font-weight: 400;
  font-size: 14px;
  color: #6b7280;
`;

const SummaryValue = styled.span<{ $bold?: boolean; $accent?: boolean }>`
  font-family: "Inter", sans-serif;
  font-weight: ${(p) => (p.$bold ? 700 : 500)};
  font-size: ${(p) => (p.$accent ? "24px" : "14px")};
  color: ${(p) => (p.$accent ? "#0099FF" : "#111827")};
`;

const TrialEndNote = styled.div`
  font-family: "Inter", sans-serif;
  font-weight: 400;
  font-size: 13px;
  color: #9ca3af;
  margin-top: 2px;
`;

const ErrorMessage = styled.div`
  color: #dc2626;
  font-family: "Inter", sans-serif;
  font-size: 14px;
  margin-top: 8px;
`;

const Card = styled.div`
  width: 480px;
  background: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow:
    0px 0px 0px 1px rgba(14, 20, 27, 0.16),
    0px 6px 12px 0px rgba(14, 20, 27, 0.06);
`;

const CardBody = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 32px;
  padding: 48px;
`;

const TextGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  text-align: center;
  width: 100%;
`;

const Heading = styled.h1`
  font-family: "Inter", sans-serif;
  font-weight: 500;
  font-size: 20px;
  line-height: 28px;
  color: #131416;
  margin: 0;
`;

const Subtitle = styled.p`
  font-family: "Inter", sans-serif;
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  color: #767a7f;
  margin: 0;
`;

const LogoutLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  font-family: "Inter", sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #767a7f;
  cursor: pointer;
  text-decoration: underline;
  text-decoration-color: #caccce;
  &:hover {
    color: #4d5156;
  }
`;

const CenteredOverlay = styled(Overlay)`
  align-items: center;
  justify-content: center;
`;

// --- Checkout Form (rendered inside Elements provider) ---

function CheckoutForm({
  billingInterval,
  builderSeats,
  onSuccess,
  buyNow,
}: {
  billingInterval: BillingInterval;
  builderSeats: number;
  onSuccess: () => void;
  buyNow?: boolean;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!stripe || !elements) return;

      setSubmitting(true);
      setError(null);

      const result = await stripe.confirmSetup({
        elements,
        redirect: "if_required",
      });

      if (result.error) {
        setError(result.error.message ?? "Payment setup failed");
        setSubmitting(false);
        return;
      }

      try {
        await callServer<{ subscriptionId: string }>(
          {
            method: HttpMethod.Post,
            url: "v1/billing/confirm-trial-subscription",
            body: {
              billingInterval,
              builderSeats,
              skipTrial: buyNow || undefined,
            },
          },
          { notifyOnError: false },
        );
        onSuccess();
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Failed to create subscription",
        );
        setSubmitting(false);
      }
    },
    [stripe, elements, billingInterval, builderSeats, buyNow, onSuccess],
  );

  return (
    <form onSubmit={handleSubmit}>
      <PaymentSection>
        <PaymentTitle>Payment details</PaymentTitle>
        <PaymentSubtitle>
          {buyNow
            ? "Your card will be charged immediately."
            : "Your card will not be charged during the trial period."}
        </PaymentSubtitle>
        <PaymentElement
          options={{
            layout: "tabs",
          }}
        />
      </PaymentSection>
      {error && <ErrorMessage data-test="cc-gate-error">{error}</ErrorMessage>}
      <SubmitButton
        data-test="cc-gate-submit-btn"
        type="submit"
        disabled={submitting || !stripe}
      >
        {submitting
          ? "Processing..."
          : buyNow
            ? "Subscribe now"
            : "Start free trial"}
      </SubmitButton>
      <Disclaimer>
        By providing your card information, you allow Superblocks to charge your
        card for future payments in accordance with their terms. You can cancel
        at any time.
      </Disclaimer>
      <Footer>
        <span>
          Powered by{" "}
          <FooterLink
            href="https://stripe.com"
            target="_blank"
            rel="noopener noreferrer"
          >
            Stripe
          </FooterLink>
        </span>
        <FooterLink
          href="https://stripe.com/legal/end-users"
          target="_blank"
          rel="noopener noreferrer"
        >
          Terms
        </FooterLink>
        <FooterLink
          href="https://stripe.com/privacy"
          target="_blank"
          rel="noopener noreferrer"
        >
          Privacy
        </FooterLink>
      </Footer>
    </form>
  );
}

// --- Main Component ---

const CreditCardGateCustom = ({ children }: { children: React.ReactNode }) => {
  const org = useSelector(selectOnlyOrganization);
  const accessMode = useSelector(getAccessMode);
  const [canManageBilling] = useAuthorizationCheck([MANAGE_BILLING]);
  const navigate = useNavigate();
  const ccGateEnabled = useFeatureFlag(Flag.ENABLE_CREDIT_CARD_GATE);
  const defaultPricing = useDefaultPricing();

  const [billingInterval, setBillingInterval] =
    useState<BillingInterval>("annual");
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const trialDisabled = Boolean(
    org?.billing?.isExpired && org?.billing?.plan === BillingPlan.TRIAL,
  );
  const [buyNow, setBuyNow] = useState(trialDisabled);
  const [builderSeats, setBuilderSeats] = useState(1);

  useEffect(() => {
    if (trialDisabled) setBuyNow(true);
  }, [trialDisabled]);

  const showCcGate =
    ccGateEnabled === true &&
    org?.billing?.showCcGateIfTrial === true &&
    org?.billing?.plan === BillingPlan.TRIAL;

  useEffect(() => {
    if (!showCcGate || !canManageBilling) return;

    let cancelled = false;
    setLoading(true);

    (async () => {
      try {
        const response = await callServer<{
          clientSecret: string;
          customerId: string;
        }>(
          {
            method: HttpMethod.Post,
            url: "v1/billing/create-trial-setup-intent",
          },
          { notifyOnError: false },
        );
        if (!cancelled && response?.clientSecret) {
          setClientSecret(response.clientSecret);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [showCcGate, canManageBilling]);

  const handleSuccess = useCallback(() => {
    setSuccess(true);
    setTimeout(() => {
      window.location.replace(
        window.location.pathname + window.location.search,
      );
    }, 1000);
  }, []);

  const handleLogout = useCallback(
    () => navigate({ pathname: LOGOUT_URL }),
    [navigate],
  );

  const trialEndDate = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + 14);
    return d.toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  }, []);

  const pricePerSeat =
    billingInterval === "annual"
      ? defaultPricing.annualPrice
      : defaultPricing.monthlyPrice;
  const effectiveSeats = buyNow ? builderSeats : 1;
  const totalPrice = pricePerSeat * effectiveSeats;
  const priceFormatted = `$${totalPrice.toFixed(2)}`;
  const monthlyPerSeat =
    billingInterval === "annual"
      ? defaultPricing.annualPrice / 12
      : defaultPricing.monthlyPrice;

  if (
    SUPERBLOCKS_UI_DEPLOYMENT_TYPE === DeploymentTypeEnum.CLOUD_PREM ||
    accessMode === AccessMode.VISITOR ||
    accessMode === AccessMode.EXTERNAL_USER ||
    !showCcGate
  ) {
    return <>{children}</>;
  }

  if (success) {
    return (
      <CenteredOverlay data-test="cc-gate-success">
        <Card>
          <CardBody>
            <LogoWrapper>
              <LogoImg src={logo} alt="Superblocks" />
            </LogoWrapper>
            <TextGroup>
              <Heading>Setting up your account...</Heading>
              <Subtitle>This will only take a moment.</Subtitle>
            </TextGroup>
            <Spin
              indicator={<LoadingOutlined style={{ fontSize: 32 }} spin />}
            />
          </CardBody>
        </Card>
      </CenteredOverlay>
    );
  }

  if (!canManageBilling) {
    return (
      <CenteredOverlay>
        <Card>
          <CardBody>
            <LogoWrapper>
              <LogoImg src={logo} alt="Superblocks" />
            </LogoWrapper>
            <TextGroup>
              <Heading>Start your free trial</Heading>
              <Subtitle>
                Your organization admin needs to set up billing before you can
                continue.
              </Subtitle>
            </TextGroup>
            <Subtitle>
              Please contact your organization administrator to set up billing
              and start the trial.
            </Subtitle>
          </CardBody>
        </Card>
        <LogoutLink
          onClick={handleLogout}
          style={{ position: "absolute", bottom: 24, right: 24 }}
        >
          Logout
        </LogoutLink>
      </CenteredOverlay>
    );
  }

  return (
    <Overlay data-test="cc-gate-overlay">
      <Header>
        <HeaderLeft>
          <LogoWrapper>
            <LogoImg src={logo} alt="Superblocks" />
          </LogoWrapper>
          <HeaderTitle>Checkout</HeaderTitle>
        </HeaderLeft>
        <TalkToSalesButton
          onClick={() =>
            window.open(AI_QUOTA_PAYWALL_CONTACT_SALES_URL, "_blank")
          }
        >
          Talk to Sales
        </TalkToSalesButton>
      </Header>
      <Content>
        <LeftPanel>
          <LeftPanelInner>
            <Title data-test="cc-gate-title">
              {buyNow ? "Buy Superblocks Teams" : "Try Free for 14 Days"}
            </Title>
            {!buyNow && (
              <TitleSubheader>
                We&apos;ll remind you before your trial ends. You can cancel
                anytime.
              </TitleSubheader>
            )}
            {!trialDisabled && (
              <SkipLink onClick={() => setBuyNow((v) => !v)}>
                {buyNow
                  ? "Or start a free trial"
                  : "Or buy Superblocks Teams now"}
              </SkipLink>
            )}

            <IntervalToggle>
              <IntervalOption
                $active={billingInterval === "annual"}
                onClick={() => setBillingInterval("annual")}
              >
                <RadioDot $active={billingInterval === "annual"} />
                <IntervalLabel>
                  Billed annually <SaveBadge>save 20%</SaveBadge>
                </IntervalLabel>
                <IntervalPrice>
                  ${defaultPricing.annualPrice.toFixed(2)}{" "}
                  <IntervalUnit>/ year</IntervalUnit>
                </IntervalPrice>
                <IntervalSub>
                  ${(defaultPricing.annualPrice / 12).toFixed(2)} / month
                </IntervalSub>
              </IntervalOption>
              <IntervalOption
                $active={billingInterval === "monthly"}
                onClick={() => setBillingInterval("monthly")}
              >
                <RadioDot $active={billingInterval === "monthly"} />
                <IntervalLabel>Billed monthly</IntervalLabel>
                <IntervalPrice>
                  ${defaultPricing.monthlyPrice.toFixed(2)}{" "}
                  <IntervalUnit>/ month</IntervalUnit>
                </IntervalPrice>
                <IntervalSub>
                  ${(defaultPricing.monthlyPrice * 12).toFixed(2)} / year
                </IntervalSub>
              </IntervalOption>
            </IntervalToggle>

            {buyNow && (
              <>
                <SectionLabel>AI Builder licenses</SectionLabel>
                <SeatSelector>
                  <StepperButton
                    onClick={() => setBuilderSeats((s) => Math.max(1, s - 1))}
                    disabled={builderSeats <= 1}
                    aria-label="Decrease licenses"
                  >
                    <MinusOutlined style={{ fontSize: 14 }} />
                  </StepperButton>
                  <SeatCount>{builderSeats}</SeatCount>
                  <StepperButton
                    onClick={() => setBuilderSeats((s) => Math.min(15, s + 1))}
                    disabled={builderSeats >= 15}
                    aria-label="Increase licenses"
                  >
                    <PlusOutlined style={{ fontSize: 14 }} />
                  </StepperButton>
                </SeatSelector>
                <IncludesNote>
                  Includes {builderSeats * AI_QUOTA_PAYWALL_CREDITS_PER_SEAT}{" "}
                  credits + 1 deployed app per team
                </IncludesNote>
              </>
            )}

            {loading && (
              <div style={{ textAlign: "center", padding: "48px 0" }}>
                <Spin
                  indicator={<LoadingOutlined style={{ fontSize: 32 }} spin />}
                />
              </div>
            )}

            {clientSecret && (
              <Elements
                stripe={stripePromise}
                options={{
                  clientSecret,
                  appearance: {
                    theme: "stripe",
                    variables: {
                      colorPrimary: "#0099FF",
                      fontFamily: '"Inter", sans-serif',
                      borderRadius: "8px",
                    },
                  },
                }}
              >
                <CheckoutForm
                  billingInterval={billingInterval}
                  builderSeats={effectiveSeats}
                  onSuccess={handleSuccess}
                  buyNow={buyNow}
                />
              </Elements>
            )}
          </LeftPanelInner>
        </LeftPanel>

        <RightPanel>
          <SummaryLabel>Summary</SummaryLabel>
          <PlanName>Superblocks Teams</PlanName>
          <PlanSubline>
            {buyNow
              ? `${effectiveSeats} AI Builder${effectiveSeats > 1 ? "s" : ""} · $${monthlyPerSeat.toFixed(2)}/mo × ${effectiveSeats} seat${effectiveSeats > 1 ? "s" : ""}`
              : `1 AI Builder · $${monthlyPerSeat.toFixed(2)}/mo`}
          </PlanSubline>

          {Object.entries(FEATURES).map(([category, items]) => (
            <div key={category}>
              <FeatureCategory>{category}</FeatureCategory>
              {items.map((item) => (
                <FeatureItem key={item}>
                  <CheckOutlined
                    style={{
                      color: "#0099FF",
                      fontSize: 12,
                      marginTop: 4,
                      flexShrink: 0,
                    }}
                  />
                  {item}
                </FeatureItem>
              ))}
            </div>
          ))}

          <Divider />

          <SummaryRow>
            <SummaryKey>Billing period</SummaryKey>
            <SummaryValue $bold>
              {billingInterval === "annual" ? "Yearly" : "Monthly"}
            </SummaryValue>
          </SummaryRow>
          {buyNow && (
            <>
              <BreakdownRow>
                <span>
                  {effectiveSeats}{" "}
                  {effectiveSeats === 1 ? "license" : "licenses"} × $
                  {monthlyPerSeat.toFixed(2)}/mo
                </span>
                <span>${(monthlyPerSeat * effectiveSeats).toFixed(2)}/mo</span>
              </BreakdownRow>
              <BreakdownRow>
                <span>
                  {effectiveSeats * AI_QUOTA_PAYWALL_CREDITS_PER_SEAT} AI
                  credits included
                </span>
              </BreakdownRow>
              <BreakdownRow>
                <span>1 deployed app per team</span>
              </BreakdownRow>
            </>
          )}
          <SummaryRow>
            <SummaryKey>Subtotal</SummaryKey>
            <SummaryValue>
              {priceFormatted}/{billingInterval === "annual" ? "yr" : "mo"}
            </SummaryValue>
          </SummaryRow>

          <Divider />

          <SummaryRow>
            <SummaryKey style={{ fontWeight: 600, color: "#111827" }}>
              {buyNow ? "Total due today" : "Billed now"}
            </SummaryKey>
            <SummaryValue $accent $bold>
              {buyNow ? priceFormatted : "$0.00"}
            </SummaryValue>
          </SummaryRow>
          {!buyNow && <TrialEndNote>Trial ends {trialEndDate}</TrialEndNote>}
        </RightPanel>
      </Content>

      <LogoutLink
        onClick={handleLogout}
        style={{ position: "absolute", bottom: 16, right: 24 }}
      >
        Logout
      </LogoutLink>
    </Overlay>
  );
};

CreditCardGateCustom.displayName = "CreditCardGateCustom";

export default CreditCardGateCustom;
